import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

const statuses = { sent: 'Sent', waiting: 'Waiting', sending: 'Sending', filtered_out: 'Filtered out', failed: 'Failed' };
const formatTime = (value) => value ? new Intl.DateTimeFormat(undefined, {
  dateStyle: 'short', timeStyle: 'long'
}).format(new Date(value)) : '—';

export default function AutomationScenarioPlayers({ scenario, onBack, onClose }) {
  const [status, setStatus] = useState('sent');
  const [cursors, setCursors] = useState([null]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const closeButton = useRef(null);
  const cursor = cursors[cursors.length - 1];

  useEffect(() => { closeButton.current?.focus(); }, []);
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError(false);
    const query = new URLSearchParams({ status });
    if (cursor) query.set('before', cursor);
    fetch(`/event-scenarios/${scenario.id}/players?${query}`, {
      headers: { Accept: 'application/json' }, signal: controller.signal
    }).then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((value) => { if (!controller.signal.aborted) setData(value); })
      .catch((err) => { if (err.name !== 'AbortError') setError(true); });
    return () => controller.abort();
  }, [scenario.id, status, cursor, revision]);

  const keyboard = (event) => {
    if (event.key === 'Escape') onClose();
    if (event.key !== 'Tab') return;
    const controls = Array.from(event.currentTarget.querySelectorAll('button:not(:disabled), select'));
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
    <section role='dialog' aria-modal='true' aria-labelledby='scenario-players-title'
      className='automation-players' onKeyDown={keyboard}>
      <header><h2 id='scenario-players-title'>Players</h2>
        <button ref={closeButton} type='button' aria-label='Close players' onClick={onClose}><X size={16} strokeWidth={1.6} /></button></header>
      <p className='automation-players__name'>{scenario.name}</p>
      <div className='automation-players__toolbar'>
        <label>Status <select value={status} onChange={(event) => { setStatus(event.target.value); setCursors([null]); }}>
          <option value='all'>All</option>
          {Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <button type='button' onClick={() => setRevision((value) => value + 1)}>Refresh</button>
      </div>
      <div className='automation-players__table' aria-live='polite'>
        {error ? <p role='alert'>Could not load players. Try Refresh.</p>
          : !data ? <p role='status'>Loading players…</p>
            : !data.players.length ? <p>No players with this status.</p>
              : <table><thead><tr><th>Player ID</th><th>Status</th><th>Sent at</th></tr></thead>
                <tbody>{data.players.map((player) => <tr key={player.id}>
                  <td className='automation-players__id'>{player.player_id}</td>
                  <td>{statuses[player.status] || player.status}</td>
                  <td className='automation-players__time'>{formatTime(player.sent_at)}</td>
                </tr>)}</tbody></table>}
      </div>
      <p className='automation-players__note'>Sent means accepted by the sender. Each row is one scenario entry.</p>
      <footer><button type='button' onClick={onBack}>Back to results</button>
        <div><button type='button' disabled={cursors.length === 1 || !data} onClick={() => setCursors((value) => value.slice(0, -1))}>Previous</button>
          <span>Page {cursors.length}</span>
          <button type='button' disabled={!data?.next_cursor} onClick={() => setCursors((value) => [...value, data.next_cursor])}>Next</button></div></footer>
    </section>
  </div>;
}
